import { Badge, HStack, Icon, Text, Tooltip, VStack } from '@elkaso-app/web-design';
import React from 'react';
import { FiCheckCircle, FiClock, FiMail, FiMapPin, FiPhone } from 'react-icons/fi';

interface BusinessCellProps {
  businessName: string;
  englishName?: string;
  primaryContact?: {
    mobile?: string;
    telephone?: string;
    fax?: string;
    email?: string;
  };
  primaryLocation?: {
    area?: string;
    subArea?: string;
    community?: string;
    address?: string;
    landmark?: string;
    parcelId?: string;
    street?: string;
    building?: string;
    floor?: string;
    unit?: string;
    gpsCoordinates?: string;
  };
  summary?: {
    totalLicenses: number;
    hasActiveLicense: boolean;
    expiringSoonCount: number;
    latestUpdate: string;
  };
}

export const BusinessCell: React.FC<BusinessCellProps> = ({
  businessName,
  englishName,
  primaryContact,
  primaryLocation,
  summary,
}) => {
  const formatTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      if (days === 0) return 'Today';
      if (days === 1) return 'Yesterday';
      if (days < 7) return `${days} days ago`;
      if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
      return `${Math.floor(days / 30)} months ago`;
    } catch {
      return 'Unknown';
    }
  };

  const getContactIcon = () => {
    if (!primaryContact) return null;
    if (primaryContact.mobile) return FiPhone;
    if (primaryContact.telephone) return FiPhone;
    if (primaryContact.email) return FiMail;
    return null;
  };

  const getContactValue = () => {
    if (!primaryContact) return null;
    return primaryContact.mobile || primaryContact.telephone || primaryContact.email || null;
  };

  const getLocationText = () => {
    if (!primaryLocation) return null;
    const parts = [primaryLocation.area, primaryLocation.subArea, primaryLocation.community].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const ContactIcon = getContactIcon();
  const contactValue = getContactValue();
  const locationText = getLocationText();

  return (
    <div style={{ height: '100px', display: 'flex', alignItems: 'center', padding: '12px 0', maxWidth: '100%' }}>
      <VStack align='start' spacing={2} maxW='100%'>
        {/* Business Name Section */}
        <VStack align='start' spacing={1} maxW='100%'>
          <Tooltip label={businessName} isDisabled={businessName.length <= 30}>
            <Text
              fontWeight='semibold'
              fontSize='md'
              color='gray.800'
              lineHeight='1.2'
              maxW='100%'
              wordBreak='break-word'
              whiteSpace='normal'
              noOfLines={2}>
              {businessName}
            </Text>
          </Tooltip>

          {englishName && (
            <Tooltip label={englishName} isDisabled={englishName.length <= 25}>
              <Text
                fontSize='sm'
                color='gray.600'
                lineHeight='1.2'
                maxW='100%'
                wordBreak='break-word'
                whiteSpace='normal'
                noOfLines={1}
                fontStyle='italic'>
                {englishName}
              </Text>
            </Tooltip>
          )}
        </VStack>

        {/* Info Section */}
        <HStack spacing={3} flexWrap='wrap'>
          {/* License Count */}
          {summary && (
            <Badge colorScheme='blue' variant='subtle' size='sm'>
              {summary.totalLicenses} License{summary.totalLicenses !== 1 ? 's' : ''}
            </Badge>
          )}

          {/* Active Status */}
          {summary?.hasActiveLicense && (
            <Badge colorScheme='green' variant='subtle' size='sm'>
              <HStack spacing={1}>
                <Icon as={FiCheckCircle} boxSize='10px' />
                <Text>Active</Text>
              </HStack>
            </Badge>
          )}

          {/* Expiring Soon Warning */}
          {summary && summary.expiringSoonCount > 0 && (
            <Badge colorScheme='orange' variant='subtle' size='sm'>
              <HStack spacing={1}>
                <Icon as={FiClock} boxSize='10px' />
                <Text>{summary.expiringSoonCount} Expiring</Text>
              </HStack>
            </Badge>
          )}

          {/* Contact Info */}
          {ContactIcon && contactValue && (
            <Badge colorScheme='purple' variant='subtle' size='sm'>
              <HStack spacing={1}>
                <Icon as={ContactIcon} boxSize='10px' />
                <Text>Contact</Text>
              </HStack>
            </Badge>
          )}

          {/* Location */}
          {locationText && (
            <Badge colorScheme='teal' variant='subtle' size='sm'>
              <HStack spacing={1}>
                <Icon as={FiMapPin} boxSize='10px' />
                <Text>Location</Text>
              </HStack>
            </Badge>
          )}

          {/* Last Update */}
          {summary && summary.latestUpdate && (
            <Badge colorScheme='gray' variant='subtle' size='sm'>
              <HStack spacing={1}>
                <Icon as={FiClock} boxSize='10px' />
                <Text>{formatTimeAgo(summary.latestUpdate)}</Text>
              </HStack>
            </Badge>
          )}
        </HStack>
      </VStack>
    </div>
  );
};
